import React from "react";
import { useIsReadOnlyMode } from "newui/features/ExecutiveView/hooks/useIsReadOnlyMode";

interface ReadOnlyGuardProps {
	/** Content to conditionally render or hide in read-only mode */
	children: React.ReactNode;
	/**
	 * Controls how the guard behaves when read-only mode is active.
	 *
	 * - `"hidden"` (default): renders `null`, removing the element entirely.
	 * - `"placeholder"`: renders the children with `visibility: hidden` and
	 *   `pointer-events: none`, preserving layout space without allowing interaction.
	 */
	as?: "hidden" | "placeholder";
}

/**
 * Conditionally renders or hides its children based on the executive
 * read-only mode state (`useIsReadOnlyMode`).
 *
 * Usage (hide a button):
 * ```tsx
 * <ReadOnlyGuard>
 *   <Button onClick={save}>Save</Button>
 * </ReadOnlyGuard>
 * ```
 *
 * Usage (preserve layout):
 * ```tsx
 * <ReadOnlyGuard as="placeholder">
 *   <Button onClick={submit}>Submit</Button>
 * </ReadOnlyGuard>
 * ```
 */
const ReadOnlyGuard = ({ children, as: mode = "hidden" }: ReadOnlyGuardProps) => {
	const isReadOnly = useIsReadOnlyMode();

	if (!isReadOnly) {
		return <>{children}</>;
	}

	if (mode === "placeholder") {
		return (
			<span
				aria-hidden="true"
				style={{ visibility: "hidden", pointerEvents: "none", display: "inline-block" }}
			>
				{children}
			</span>
		);
	}

	return null;
};

export { ReadOnlyGuard };
export type { ReadOnlyGuardProps };
